import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Zap, ShieldCheck, Headphones, Link2, MessageCircle, Send } from "lucide-react";
import { IMG } from "@/lib/assets";
import { useSiteSettings, waLink } from "@/context/SiteSettingsContext";
import { useAppearance, siteImg } from "@/context/AppearanceContext";
import { HomeMarquee } from "@/components/HomeMarquee";

const SLIDES = [
  { id: "d1", img: IMG.bannerHiggs, title: "Higgs Game Island" },
  { id: "d2", img: IMG.bannerRoyal, title: "Royal Dream" },
];

const WaIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.2h.01c5.46 0 9.91-4.45 9.91-9.91A9.85 9.85 0 0 0 12.04 2Zm5.8 14.03c-.25.69-1.44 1.32-2 1.4-.51.08-1.16.11-1.87-.12-.43-.14-.98-.32-1.69-.62-2.97-1.28-4.91-4.27-5.06-4.47-.15-.2-1.21-1.61-1.21-3.07 0-1.46.77-2.18 1.04-2.48.27-.3.6-.37.8-.37h.57c.18 0 .43-.07.67.51.25.6.84 2.06.92 2.21.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.17-.31.39-.45.52-.15.15-.3.31-.13.6.17.3.77 1.27 1.65 2.06 1.14 1.01 2.1 1.33 2.4 1.48.3.15.47.12.65-.07.17-.2.74-.87.94-1.17.2-.3.4-.25.67-.15.27.1 1.73.82 2.03.97.3.15.5.22.57.35.08.12.08.72-.17 1.4Z" />
  </svg>
);

const Slide = ({ s }) => {
  const body = (
    <>
      {s.custom ? (
        <picture>
          {s.mobile ? <source media="(max-width: 640px)" srcSet={s.mobile} /> : null}
          <img src={s.img} alt={s.title || "Banner JP STORE"} className="h-full w-full object-cover" />
        </picture>
      ) : <img src={s.img} alt={s.title} className="h-full w-full object-cover" />}
      {s.title ? (
        <>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <span className="kz-display absolute bottom-3 left-4 text-lg text-white sm:text-2xl">{s.title}</span>
        </>
      ) : null}
    </>
  );
  const cls = `relative block w-full shrink-0 ${s.custom ? (s.mobile ? "aspect-[4/5] sm:aspect-[16/5]" : "aspect-[16/5]") : "aspect-[16/7]"}`;
  if (!s.link) return <div className={cls}>{body}</div>;
  return s.link.startsWith("/") ? <Link to={s.link} className={cls}>{body}</Link> : <a href={s.link} target="_blank" rel="noopener noreferrer" className={cls}>{body}</a>;
};

const Carousel = () => {
  const { banners } = useAppearance();
  const slides = banners.length
    ? banners.map((b) => ({ id: b.id, img: siteImg(b.desktop_url), mobile: siteImg(b.mobile_url), title: b.title, link: b.link, custom: true }))
    : SLIDES;
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % slides.length), 4500);
    return () => clearInterval(t);
  }, [slides.length]);
  const cur = i % slides.length;
  return (
    <div className="relative overflow-hidden rounded-b-3xl" data-testid="hero-carousel">
      <div className="flex transition-transform duration-700 ease-out" style={{ transform: `translateX(-${cur * 100}%)` }}>
        {slides.map((s) => <Slide key={s.id} s={s} />)}
      </div>
      <div className="absolute bottom-3 right-4 flex gap-1.5">
        {slides.map((s, idx) => (
          <button
            key={s.id}
            onClick={() => setI(idx)}
            className={`h-1.5 rounded-full transition-all ${idx === cur ? "w-5 bg-[var(--jp-primary)]" : "w-1.5 bg-white/60"}`}
            aria-label={`Slide ${idx + 1}`}
            data-testid={`carousel-dot-${idx}`}
          />
        ))}
      </div>
    </div>
  );
};

const PromoBanner = () => {
  const { promo, promo_link } = useAppearance();
  if (!promo) return null;
  const img = <img src={siteImg(promo)} alt="Promo JP STORE" className="aspect-square w-full rounded-2xl border border-white/10 object-cover" data-testid="promo-banner" />;
  return (
    <div className="mx-auto mt-6 max-w-md px-4">
      {!promo_link ? img : promo_link.startsWith("/") ? <Link to={promo_link}>{img}</Link> : <a href={promo_link} target="_blank" rel="noopener noreferrer">{img}</a>}
    </div>
  );
};

const ActionLinks = () => {
  const { support_wa, wa_channel, bongkar_otomatis_url, live_chat_url, telegram_url } = useSiteSettings();
  const ext = { target: "_blank", rel: "noopener noreferrer" };
  return (
  <div className="mx-auto mt-6 flex max-w-md flex-col gap-2 px-4">
    <a href={waLink(support_wa, "Halo Admin JP STORE, saya ingin komplain transaksi.")} target="_blank" rel="noopener noreferrer" className="kz-pill bg-gradient-to-r from-[#16a34a] to-[#22c55e] text-white" data-testid="link-wa-komplain">
      <WaIcon /> WA KHUSUS ADMIN KOMPLAIN (DIBACA)
    </a>
    <a href={wa_channel || waLink(support_wa)} target="_blank" rel="noopener noreferrer" className="kz-pill kz-gold-btn text-black" data-testid="link-join-saluran">
      <WaIcon /> JOIN SALURAN
    </a>
    {bongkar_otomatis_url ? (
      <a href={bongkar_otomatis_url} {...ext} className="kz-pill bg-gradient-to-r from-[#dc2626] to-[#ef4444] text-white" data-testid="link-bongkar-otomatis">
        <Link2 size={14} /> BONGKAR OTOMATIS
      </a>
    ) : (
      <Link to="/bongkar" className="kz-pill bg-gradient-to-r from-[#dc2626] to-[#ef4444] text-white" data-testid="link-bongkar-otomatis">
        <Link2 size={14} /> BONGKAR OTOMATIS
      </Link>
    )}
    {live_chat_url || telegram_url ? (
      <div className="grid grid-cols-2 gap-2">
        {live_chat_url ? (
          <a href={live_chat_url} {...ext} className={`kz-pill bg-gradient-to-r from-[#7c3aed] to-[#a855f7] text-white ${telegram_url ? "" : "col-span-2"}`} data-testid="link-live-chat">
            <MessageCircle size={14} /> LIVE CHAT
          </a>
        ) : null}
        {telegram_url ? (
          <a href={telegram_url} {...ext} className={`kz-pill bg-gradient-to-r from-[#0284c7] to-[#38bdf8] text-white ${live_chat_url ? "" : "col-span-2"}`} data-testid="link-telegram">
            <Send size={14} /> TELEGRAM
          </a>
        ) : null}
      </div>
    ) : null}
  </div>
  );
};

const CARDS = [
  { slug: "topup/royaldream", pid: "royal_dream", name: "Royal Dream", img: IMG.rdChar, icon: IMG.rdCoin, btnCls: "bg-[#22e30f] text-black", testid: "product-card-royal_dream" },
  { slug: "topup/higgsdomino", pid: "higgs_domino", name: "Higgs Domino", img: IMG.higgsChar, icon: IMG.higgsTile, btnCls: "bg-[#ff1fd2] text-white", testid: "product-card-higgs_domino" },
];

const readableOn = (hex) => {
  const n = parseInt((hex || "#e5b84a").slice(1), 16);
  const lum = (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  return lum > 0.6 ? "#111111" : "#ffffff";
};

const ProductCard = ({ c, t, primary }) => (
  <Link to={`/${c.slug}`} className="group relative block overflow-hidden rounded-2xl border border-[#3a3220]" data-testid={c.testid}>
    <img src={c.img} alt={c.name} className="aspect-[4/4.2] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
    {t.badge ? <span className="absolute left-3 top-3 rounded-md bg-[var(--jp-primary)] px-2 py-0.5 text-[9px] font-extrabold" style={{ color: readableOn(primary) }} data-testid={`product-badge-${c.pid}`}>{t.badge}</span> : null}
    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-3 sm:p-4">
      <div>
        <h3 className="kz-display text-lg text-white sm:text-xl">{c.name}</h3>
        <p className="mt-0.5 text-[9px] font-semibold text-white/80 sm:text-[10px]">{t.sub}</p>
        <span className={`jp-btn-radius mt-2.5 inline-flex items-center gap-1 px-3 py-1.5 text-[9px] font-extrabold sm:text-[10px] ${c.btnCls}`} data-testid={`pesan-sekarang-${c.pid}`}>
          PESAN SEKARANG <ChevronRight size={12} />
        </span>
      </div>
      <img src={c.icon} alt="" className="h-10 w-10 rounded-lg object-cover ring-1 ring-white/30 sm:h-12 sm:w-12" />
    </div>
  </Link>
);

const FEATURES = [
  { icon: Zap, color: "text-orange-500", id: "cepat" },
  { icon: ShieldCheck, color: "text-blue-500", id: "aman" },
  { icon: Headphones, color: "text-zinc-700", id: "support" },
];

const splitBrand = (title) => {
  const idx = title.toUpperCase().lastIndexOf("JP STORE");
  return idx < 0 ? [title, ""] : [title.slice(0, idx), title.slice(idx)];
};

const Services = ({ t }) => {
  const { theme, cards } = useAppearance();
  const { games_enabled: ge } = useSiteSettings();
  const active = CARDS.filter((c) => ge?.[c.pid] !== false);
  const [lead, brand] = splitBrand(t.services_title);
  return (
    <section className="mt-10 px-4 text-center">
      <h2 className="kz-display text-xl text-white sm:text-2xl" data-testid="services-title">
        {lead}{brand ? <span className="text-[var(--jp-primary)]">{brand}</span> : null}
      </h2>
      <p className="mt-1 text-[11px] text-white/80" data-testid="services-subtitle">{t.services_subtitle}</p>
      <div className={`mt-5 grid gap-3 text-left ${active.length > 1 ? "grid-cols-2" : "mx-auto max-w-xs grid-cols-1"}`}>
        {active.map((c) => <ProductCard key={c.slug} c={{ ...c, img: siteImg(cards?.[c.pid]?.image) || c.img, icon: siteImg(cards?.[c.pid]?.icon) || c.icon }} t={t.products[c.pid]} primary={theme.primary} />)}
      </div>
    </section>
  );
};

const Brand = ({ t }) => (
  <section className="mt-12 px-4 text-center">
    <h2 className="kz-display text-2xl text-white sm:text-3xl">
      JP <span className="text-[var(--jp-primary)]">STORE</span>
    </h2>
    <p className="kz-display mt-1 text-base text-white/90" data-testid="brand-tagline">{t.brand_tagline}</p>
    <div className="mt-6 grid gap-3 sm:grid-cols-3">
      {FEATURES.map(({ icon: Icon, color, id }, idx) => (
        <div key={id} className="rounded-xl border border-[#3a3220] bg-[#111] p-5" data-testid={`feature-${id}`}>
          <div className="mx-auto grid h-11 w-11 place-items-center rounded-xl bg-white">
            <Icon size={22} className={color} />
          </div>
          <div className="mt-3 text-sm font-extrabold text-white">{t.features[idx]?.title}</div>
          <div className="mt-1 text-[11px] text-white/70">{t.features[idx]?.desc}</div>
        </div>
      ))}
    </div>
  </section>
);

export const Home = () => {
  const { texts } = useAppearance();
  return (
    <main className="relative z-10 pb-28" data-testid="home-page">
      <div className="mx-auto max-w-3xl">
        <HomeMarquee />
        <Carousel />
        <ActionLinks />
        <PromoBanner />
        {texts ? <Services t={texts} /> : null}
        {texts ? <Brand t={texts} /> : null}
      </div>
    </main>
  );
};
